import styled, { CSSObject } from '@emotion/styled'
import { ReactNode } from 'react'

import { Body } from './Body'
import { Footer } from './Footer'
import { Header } from './Header'
import { ActionCardVariations } from './types'
import { Color, theme } from '../../constants'
import { Dialog } from '../Dialog'
import { Flex } from '../Flex'
import { Paper } from '../Paper'
import { type ActionCardAction, Actions } from './Actions'

const stackCountSharedStyles: CSSObject = {
  content: '""',
  position: 'absolute',
  height: '100%',
  width: '100%',
  backgroundColor: Color.WHITE,
  border: `1px solid ${theme.color.border}`,
  borderRadius: theme.border.radius.normal,
  boxShadow: theme.boxShadow.elevated,
}

const templateStyles = {
  display: 'grid',
  gridTemplateAreas: `
    "Header Actions"
    "Body Body"
    "Footer Footer"
  `,
  gridTemplateColumns: '1fr auto',
  gridTemplateRows: 'auto 1fr auto',
}

const CardWrapper = styled(Paper)<{ stackCount?: number; width: number }>(
  ({ stackCount, width }) => ({
    ...templateStyles,
    width,
    maxHeight: '95vh',
    position: 'relative',
    boxShadow: theme.boxShadow.elevated,
    ...(stackCount && stackCount > 1
      ? {
          '::before': {
            ...stackCountSharedStyles,
            top: 6,
            right: -6,
            zIndex: -1,
          },
        }
      : {}),
    ...(stackCount && stackCount > 2
      ? {
          '::after': {
            ...stackCountSharedStyles,
            top: 12,
            right: -12,
            zIndex: -2,
          },
        }
      : {}),
  }),
)

const SidebarWrapper = styled(Flex)<{ width: number }>(({ width }) => ({
  ...templateStyles,
  background: 'white',
  minWidth: width,
  height: '100%',
  flex: 1,
  boxShadow: theme.boxShadow.elevated,
  zIndex: theme.zIndex.contentpagesidebar,
  flexDirection: 'column',
}))

type ActionCardProps = {
  className?: string
  children?: ReactNode
  stackCount?: number
  as: ActionCardVariations
  width?: number
  icons?: ActionCardAction[]
  onClose?: () => void
}

const ActionCard = ({
  className,
  children,
  stackCount,
  as,
  width = 360,
  icons,
  onClose,
}: ActionCardProps) => {
  const Root = as === 'sidebar' ? SidebarWrapper : CardWrapper

  return (
    <Root
      as={as === 'dialog' ? Dialog.Panel : undefined}
      className={className}
      stackCount={stackCount}
      width={width}
    >
      {children}

      <Actions as={as} icons={icons} onClose={onClose} />
    </Root>
  )
}

ActionCard.Header = Header
ActionCard.Body = Body
ActionCard.Footer = Footer

export { ActionCard }
export type { ActionCardAction }
